import React, { useState, useEffect } from 'react';
import {
  Text,
  TextInput,
  TouchableOpacity,
  Modal,
  View,
  ActivityIndicator,
  ScrollView,
} from 'react-native';
import { Picker } from '@react-native-picker/picker';
import { FontAwesome5 } from '@expo/vector-icons';

import { createPriceInclusion, updatePriceInclusion, deletePriceInclusion, getPriceInclusionsByTable, getPriceInclusionById, getHeaderData } from '../../../../../api/Price';
import BasicModalContainer from '../../../../../common/components/basicmodal/BasicModalContainer';
import ModalHeader from '../../../../../common/components/basicmodal/ModalHeader';
import ModalBody from '../../../../../common/components/basicmodal/ModalBody';
import ModalFooter from '../../../../../common/components/basicmodal/ModalFooter';
import { msgStr } from '../../../../../common/constants/Message';
import { useAlertModal, useConfirmModal } from '../../../../../common/hooks';
import { commonModalStyle } from '../../../../../common/components/basicmodal';

const PackageModal = ({ 
  isModalVisible, 
  closeModal, 
  tableId, 
  groupId, 
  pointId, 
  groupName, 
  pointName,
  onSuccess 
}) => {
  const [isUpdate, setIsUpdate] = useState(false);
  const [editingPackageId, setEditingPackageId] = useState(null);
  const { showAlert } = useAlertModal();
  const { showConfirm } = useConfirmModal();
  
  const [ValidMessage, setValidMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [packagesData, setPackagesData] = useState([]);
  const [durationOptions, setDurationOptions] = useState([]);
  const [selectedPointId, setSelectedPointId] = useState(pointId || null);
  const [alertBanner, setAlertBanner] = useState({ visible: false, message: '', type: 'success' });
  const [formValues, setFormValues] = useState<any>({
    includedMiles: '',
    includedBattery: '',
    extraMilePrice: '',
  });

  useEffect(() => {
    if (isModalVisible) {
      loadPackagesData();
      loadDurationOptions();
      // Reset to add mode when modal opens
      setIsUpdate(false);
      setEditingPackageId(null);
      setFormValues({
        includedMiles: '',
        includedBattery: '',
        extraMilePrice: '',
      });
      setSelectedPointId(null);
    }
  }, [isModalVisible]);

  const loadDurationOptions = () => {
    if (!tableId) return;
    
    getHeaderData(tableId, (jsonRes, status, error) => {
      switch (status) {
        case 200:
          setDurationOptions(jsonRes || []);
          break;
        case 500:
          showAlert('error', 'Server error');
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', 'Unknown error');
          break;
      }
    });
  };

  const loadPackagesData = () => {
    if (!tableId) return;
    
    getPriceInclusionsByTable(tableId, (jsonRes, status, error) => {
      switch (status) {
        case 200:
          setPackagesData(jsonRes || []);
          break;
        case 500:
          showAlert('error', 'Server error');
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', 'Unknown error');
          break;
      }
    });
  };

  const loadExistingPackage = () => {
    if (!groupId || !pointId) return;
    
    setIsLoading(true);
    // Since we don't have a direct API to get by group and point, we'll need to find it in the packages data
    // For now, let's assume we need to search through the packages data
    getPriceInclusionsByTable(tableId, (jsonRes, status, error) => {
      setIsLoading(false);
      switch (status) {
        case 200:
          if (jsonRes && Array.isArray(jsonRes)) {
            const existingPackage = jsonRes.find(pkg => 
              pkg.group_id == groupId && pkg.point_id == pointId
            );
            if (existingPackage) {
              setFormValues({
                includedMiles: existingPackage.included_miles?.toString() || '',
                includedBattery: existingPackage.included_battery_percent?.toString() || '',
                extraMilePrice: existingPackage.extra_mile_price?.toString() || '',
              });
            } else {
              // No existing package, set up for new entry
              setFormValues({
                includedMiles: '',
                includedBattery: '',
                extraMilePrice: '',
              });
            }
          }
          break;
        case 500:
          showAlert('error', 'Server error');
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', 'Unknown error');
          break;
      }
    });
  };

  const updateFormValues = (key, value) => {
    setFormValues(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const showAlertBanner = (message, type = 'success') => {
    setAlertBanner({ visible: true, message, type });
    setTimeout(() => {
      setAlertBanner(prev => ({ ...prev, visible: false }));
    }, 3000);
  };

  const checkInput = () => {
    if (!formValues.includedMiles || parseFloat(formValues.includedMiles) < 0) {
      setValidMessage('Please enter valid included miles (≥ 0)');
      return false;
    }
    if (!formValues.includedBattery || parseFloat(formValues.includedBattery) < 0 || parseFloat(formValues.includedBattery) > 100) {
      setValidMessage('Please enter valid included battery (0-100%)');
      return false;
    }
    if (!formValues.extraMilePrice || parseFloat(formValues.extraMilePrice) < 0) {
      setValidMessage('Please enter valid extra mile price (≥ 0)');
      return false;
    }
    setValidMessage('');
    return true;
  };

  const saveButtonHandler = () => {
    if(!checkInput()) return;

    if (!selectedPointId) {
      setValidMessage('Please select a duration');
      return;
    }

    setIsLoading(true);

    const payload = {
      table_id: tableId,
      group_id: groupId,
      point_id: selectedPointId,
      included_miles: parseFloat(formValues.includedMiles),
      included_battery_percent: parseFloat(formValues.includedBattery),
      extra_mile_price: parseFloat(formValues.extraMilePrice)
    };

    const handleResponse = (jsonRes, status) => {
      switch (status) {
        case 200:
        case 201:
          showAlertBanner(isUpdate ? 'Package updated successfully' : 'Package created successfully', 'success');
          onSuccess();
          loadPackagesData(); // Refresh packages data
          // Reset to add mode after successful operation
          setIsUpdate(false);
          setEditingPackageId(null);
          setFormValues({
            includedMiles: '',
            includedBattery: '',
            extraMilePrice: '',
          });
          setSelectedPointId(null);
          setValidMessage('');
          break;
        default:
          showAlertBanner(jsonRes?.error || msgStr('unknownError'), 'error');
          break;
      }
      setIsLoading(false);
    };

    if (isUpdate && editingPackageId) {
      // Update existing package
      updatePriceInclusion(editingPackageId, payload, handleResponse);
    } else {
      // Create new package
      createPriceInclusion(payload, handleResponse);
    }
  };

  const editPackage = (packageData) => {
    setIsUpdate(true);
    setEditingPackageId(packageData.id);
    setSelectedPointId(packageData.point_id);
    setFormValues({
      includedMiles: packageData.included_miles?.toString() || '',
      includedBattery: packageData.included_battery_percent?.toString() || '',
      extraMilePrice: packageData.extra_mile_price?.toString() || '',
    });
    setValidMessage('');
  };

  const deletePackage = (packageId) => {
    showConfirm('Are you sure you want to delete this package?', () => {
      deletePriceInclusion(packageId, (jsonRes, status, error) => {
        switch (status) {
          case 200:
            showAlertBanner('Package deleted successfully', 'success');
            loadPackagesData(); // Refresh packages data
            break;
          default:
            showAlertBanner(jsonRes?.error || msgStr('unknownError'), 'error');
            break;
        }
      });
    });
  };

  return (
    <Modal
      animationType="none"
      transparent={true}
      visible={isModalVisible}
    >
      <BasicModalContainer contentStyle={{ width: '90%', maxWidth: 960 }}>
        <ModalHeader label={isUpdate ? 'Update' : 'Add' + ' Package'} closeModal={closeModal} />
        {/* Alert Banner */}
        {alertBanner.visible && (
          <View style={[
            styles.alertBanner,
            alertBanner.type === 'success' ? styles.successBanner : styles.errorBanner
          ]}>
            <Text style={[
              styles.alertBannerText,
              alertBanner.type === 'success' ? styles.successText : styles.errorText
            ]}>
              {alertBanner.message}
            </Text>
          </View>
        )}
        <ModalBody>
          <ScrollView>
            {/* Input Fields Section - Two Column Layout */}
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 15 }}>
              <View style={{ flex: 1 }}>
                <Text style={styles.label}>Group</Text>
                <TextInput
                  style={[styles.input, { backgroundColor: '#f5f5f5' }]}
                  placeholder="Group Name"
                  value={groupName || ''}
                  editable={false}
                  placeholderTextColor="#ccc"
                />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.label}>Duration</Text>
                <Picker
                  selectedValue={selectedPointId}
                  style={[styles.input]}
                  onValueChange={(itemValue) => setSelectedPointId(itemValue)}
                >
                  <Picker.Item label="Select duration" value={null} />
                  {durationOptions.map((duration) => {
                    const durationTypeText = duration.duration_type === 1 ? 'Hours' : 
                                           duration.duration_type === 2 ? 'Days' : 
                                           duration.duration_type === 3 ? 'Weeks' : 
                                           duration.duration_type;
                    return (
                      <Picker.Item 
                        key={duration.id}
                        label={`${duration.duration} ${durationTypeText}`} 
                        value={duration.id} 
                      />
                    );
                  })}
                </Picker>
              </View>
            </View>

            <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 15, marginTop: 8 }}>
              <View style={{ flex: 1 }}>
                <Text style={styles.label}>Included Miles</Text>
                <TextInput
                  style={styles.input}
                  placeholder="Enter included miles"
                  value={formValues.includedMiles}
                  onChangeText={val=>updateFormValues('includedMiles', val)}
                  placeholderTextColor="#ccc"
                  keyboardType="numeric"
                  onBlur={checkInput}
                  onSubmitEditing={saveButtonHandler}
                />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.label}>Included Battery (%)</Text>
                <TextInput
                  style={styles.input}
                  placeholder="Enter included battery percentage"
                  value={formValues.includedBattery}
                  onChangeText={val=>updateFormValues('includedBattery', val)}
                  placeholderTextColor="#ccc"
                  keyboardType="numeric"
                  onBlur={checkInput}
                  onSubmitEditing={saveButtonHandler}
                />
              </View>
            </View>

            <View style={{ marginTop: 8 }}>
              <Text style={styles.label}>Extra Mile Price</Text>
              <TextInput
                style={styles.input}
                placeholder="Enter extra mile price"
                value={formValues.extraMilePrice}
                onChangeText={val=>updateFormValues('extraMilePrice', val)}
                placeholderTextColor="#ccc"
                keyboardType="numeric"
                onBlur={checkInput}
                onSubmitEditing={saveButtonHandler}
              />
            </View>

            {ValidMessage.trim() != '' && <Text style={styles.message}>{ValidMessage}</Text>}

            {/* Packages Table Section */}
            <View style={{ marginTop: 20 }}>
              <Text style={styles.sectionHeader}>All Packages</Text>
              <View style={{ borderWidth: 1, borderColor: '#ddd', borderRadius: 5 }}>
                {/* Table Header */}
                <View style={{ flexDirection: 'row', backgroundColor: '#f5f5f5', borderBottomWidth: 1, borderBottomColor: '#ddd', padding: 10 }}>
                  <Text style={{ flex: 2, fontWeight: 'bold' }}>Group</Text>
                  <Text style={{ flex: 1, fontWeight: 'bold' }}>Duration</Text>
                  <Text style={{ flex: 1, fontWeight: 'bold' }}>Miles</Text>
                  <Text style={{ flex: 1, fontWeight: 'bold' }}>Battery</Text>
                  <Text style={{ flex: 1, fontWeight: 'bold' }}>Price</Text>
                  <Text style={{ flex: 1, fontWeight: 'bold', textAlign: 'center' }}>Actions</Text>
                </View>
                
                {/* Table Data */}
                {packagesData.map((pkg, index) => {
                  const durationTypeText = pkg.pricePoint?.duration_type === 1 ? 'Hours' : 
                                         pkg.pricePoint?.duration_type === 2 ? 'Days' : 
                                         pkg.pricePoint?.duration_type === 3 ? 'Weeks' : 
                                         pkg.pricePoint?.duration_type || '';
                  return (
                    <View key={index} style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#eee', padding: 8 }}>
                      <Text style={{ flex: 2 }}>{pkg.priceGroup?.price_group || 'N/A'}</Text>
                      <Text style={{ flex: 1 }}>{pkg.pricePoint?.duration} {durationTypeText}</Text>
                      <Text style={{ flex: 1 }}>{pkg.included_miles || '0'}</Text>
                      <Text style={{ flex: 1 }}>{pkg.included_battery_percent || '0'}%</Text>
                      <Text style={{ flex: 1 }}>${pkg.extra_mile_price || '0'}</Text>
                      <View style={{ flex: 1, flexDirection: 'row', justifyContent: 'center', gap: 8 }}>
                        <TouchableOpacity
                          onPress={() => editPackage(pkg)}
                          style={{ padding: 5 }}
                        >
                          <FontAwesome5 name="edit" size={14} color="#2e96e1" />
                        </TouchableOpacity>
                        <TouchableOpacity
                          onPress={() => deletePackage(pkg.id)}
                          style={{ padding: 5 }}
                        >
                          <FontAwesome5 name="trash" size={14} color="#dc3545" />
                        </TouchableOpacity>
                      </View>
                    </View>
                  );
                })}
                
                {packagesData.length === 0 && (
                  <View style={{ padding: 20, alignItems: 'center' }}>
                    <Text style={{ color: '#666' }}>No packages found</Text>
                  </View>
                )}
              </View>
            </View>
          </ScrollView>
        </ModalBody>
        <ModalFooter>
          <TouchableOpacity onPress={saveButtonHandler}>
            <Text style={styles.addButton}>{isUpdate ? 'Update' : 'Add'}</Text>
          </TouchableOpacity>
        </ModalFooter>
      </BasicModalContainer>
      {isLoading && (
        <View style={styles.overlay}>
          <ActivityIndicator size="large" color="#0000ff" />
        </View>
      )}
    </Modal>
  );
};

const styles = {
  ...commonModalStyle,
  alertBanner: {
    padding: 12,
    borderRadius: 6,
    marginBottom: 15,
    borderWidth: 1,
  },
  successBanner: {
    backgroundColor: '#d4edda',
    borderColor: '#c3e6cb',
  },
  errorBanner: {
    backgroundColor: '#f8d7da',
    borderColor: '#f5c6cb',
  },
  alertBannerText: {
    fontSize: 14,
    fontWeight: '600' as const,
    textAlign: 'center' as const,
  },
  successText: {
    color: '#155724',
  },
  errorText: {
    color: '#721c24',
  },
};

export default PackageModal;
